import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  useMediaQuery,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { tokens } from '../../theme';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRunningConfigurations,
  createRunningConfiguration,
  updateRunningConfiguration
} from '../../api/runningApi';
import { clearRunningConfigCache } from '../../services/runningConfigService';
import GarminConnectionCard from '../../components/garmin/GarminConnectionCard';

const RunningProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // UI state
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Configuration state
  const [configId, setConfigId] = useState(null);
  const [bodyWeight, setBodyWeight] = useState(70.0);
  const [thresholdPace, setThresholdPace] = useState(5.0);
  const [thresholdHeartRate, setThresholdHeartRate] = useState('');
  const [maxHeartRate, setMaxHeartRate] = useState(185);
  const [restingHeartRate, setRestingHeartRate] = useState('');

  // Load configuration on mount
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadConfig = async () => {
      setLoading(true);
      try {
        const configs = await getRunningConfigurations();
        if (configs && configs.length > 0) {
          const config = configs[0];
          setConfigId(config.id);
          setBodyWeight(config.body_weight || 70.0);
          setThresholdPace(config.threshold_pace || 5.0);
          setThresholdHeartRate(config.threshold_heart_rate || '');
          setMaxHeartRate(config.max_heart_rate || 185);
          setRestingHeartRate(config.resting_heart_rate || '');
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        setError('Failed to load your running configuration');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [isAuthenticated]);

  // Handle save
  const handleSave = async () => {
    if (!isAuthenticated) {
      setError('Please login to save your configuration');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const config = {
        body_weight: parseFloat(bodyWeight),
        threshold_pace: parseFloat(thresholdPace),
        threshold_heart_rate: thresholdHeartRate ? parseInt(thresholdHeartRate, 10) : null,
        max_heart_rate: parseInt(maxHeartRate, 10),
        resting_heart_rate: restingHeartRate ? parseInt(restingHeartRate, 10) : null
      };

      if (configId) {
        await updateRunningConfiguration(configId, config);
      } else {
        const created = await createRunningConfiguration(config);
        setConfigId(created.id);
      }

      clearRunningConfigCache();
      setSuccess('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setError(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (!isAuthenticated) {
    return (
      <Box m={isMobile ? "10px" : "20px"}>
        <Alert severity="warning">
          {t('pleaseLogin', 'Please login to access your running profile')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h2" fontWeight="bold" color={colors.grey[100]} mb={1}>
          {t('runningProfile', 'Running Profile')}
        </Typography>
        <Typography variant="h6" color={colors.grey[300]}>
          {user?.email}
        </Typography>
      </Box>

      {/* Notifications */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper
        elevation={3}
        sx={{
          backgroundColor: colors.grey[800],
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            backgroundColor: colors.grey[900],
            '& .MuiTab-root': {
              color: colors.grey[300],
              '&.Mui-selected': {
                color: colors.blueAccent[500]
              }
            }
          }}
        >
          <Tab label={t('runningConfiguration', 'Running Configuration')} />
          <Tab label={t('account', 'Account')} />
        </Tabs>

        {/* Tab Panels */}
        <Box p={isMobile ? 2 : 3}>
          {/* Configuration Tab */}
          {currentTab === 0 && (
            <Box>
              <Typography variant="h5" color={colors.grey[100]} mb={3}>
                {t('runningParameters', 'Running Parameters')}
              </Typography>

              <Box display="grid" gridTemplateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"} gap={2} mb={3}>
                <TextField
                  label={t('bodyWeight', 'Body Weight (kg)')}
                  type="number"
                  value={bodyWeight}
                  onChange={(e) => setBodyWeight(e.target.value)}
                  inputProps={{ step: 0.1, min: 40, max: 150 }}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label={t('thresholdPace', 'Threshold Pace (min/km)')}
                  type="number"
                  value={thresholdPace}
                  onChange={(e) => setThresholdPace(e.target.value)}
                  inputProps={{ step: 0.1, min: 3, max: 10 }}
                  helperText={t('thresholdPaceHelp', 'Your best recent 5K pace + ~15s/km is a good estimate')}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label={t('thresholdHR', 'Threshold Heart Rate (bpm)')}
                  type="number"
                  value={thresholdHeartRate}
                  onChange={(e) => setThresholdHeartRate(e.target.value)}
                  inputProps={{ min: 100, max: 220 }}
                  helperText={t('thresholdHRHelp', 'Your average HR during a 30-min all-out effort')}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label={t('maxHR', 'Max Heart Rate (bpm)')}
                  type="number"
                  value={maxHeartRate}
                  onChange={(e) => setMaxHeartRate(e.target.value)}
                  inputProps={{ min: 120, max: 220 }}
                  helperText={t('maxHRHelp', '220 - age is a rough estimate')}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label={t('restingHR', 'Resting Heart Rate (bpm)')}
                  type="number"
                  value={restingHeartRate}
                  onChange={(e) => setRestingHeartRate(e.target.value)}
                  inputProps={{ min: 30, max: 100 }}
                  helperText={t('restingHRHelp', 'Optional')}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                fullWidth
                size="large"
                sx={{
                  py: 1.5,
                  backgroundColor: colors.blueAccent[700],
                  '&:hover': {
                    backgroundColor: colors.blueAccent[600]
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : t('saveConfiguration', 'Save Configuration')}
              </Button>
            </Box>
          )}

          {/* Account Tab */}
          {currentTab === 1 && (
            <Box>
              <Typography variant="h5" color={colors.grey[100]} mb={3}>
                {t('accountSettings', 'Account Settings')}
              </Typography>
              <Typography variant="body1" color={colors.grey[300]}>
                {t('accountInfoShared', 'Account settings are shared across bike and running modules.')}
              </Typography>
              <Typography variant="body2" color={colors.grey[400]} mt={2}>
                {t('goToMainProfile', 'Go to the main Profile page to manage your account.')}
              </Typography>

              <Divider sx={{ my: 4, borderColor: colors.grey[700] }} />

              <Typography variant="h5" color={colors.grey[100]} mb={2}>
                {t('connectedAccounts', 'Connected Accounts')}
              </Typography>
              <GarminConnectionCard />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default RunningProfile;
